import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermisos } from '../hooks/usePermisos';
import { ShieldAlert, Home, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

/**
 * Componente de Protección de Rutas
 * Verifica que el usuario tenga permiso para acceder a una sección específica
 */
const ProtectedRoute = ({ 
  children, 
  moduloId, 
  seccionId, 
  requiredPermission = 'ver',
  fallbackPath = '/'
}) => {
  const { user, isAuthenticated } = useAuth();
  const { checkPermiso, isAdmin, loading } = usePermisos();
  const location = useLocation();
  const [hasPermission, setHasPermission] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const verifyPermission = async () => {
      // Si no está autenticado, redirigir a login
      if (!isAuthenticated || !user) {
        setHasPermission(false);
        setChecking(false);
        return;
      }

      // Admins siempre tienen acceso
      if (isAdmin()) {
        setHasPermission(true);
        setChecking(false);
        return;
      }

      // Si no hay moduloId o seccionId, permitir acceso (rutas públicas del módulo)
      // Esto cubre dashboards, páginas sin sección específica, etc.
      if (!moduloId || !seccionId) {
        setHasPermission(true);
        setChecking(false);
        return;
      }

      try {
        const permitted = await checkPermiso(moduloId, seccionId, requiredPermission);
        setHasPermission(permitted);
      } catch (error) {
        console.error('Error verificando permisos:', error);
        // En caso de error de verificación, permitir acceso para no bloquear la navegación
        // El backend igualmente validará los permisos en cada request
        setHasPermission(true);
      } finally {
        setChecking(false);
      }
    };

    verifyPermission();
  }, [user, isAuthenticated, moduloId, seccionId, requiredPermission, checkPermiso, isAdmin]);

  // Mostrar loading mientras verifica
  if (checking || loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-500 text-sm">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Si no tiene permiso, mostrar página de acceso denegado
  if (hasPermission === false) {
    return <AccessDeniedPage fallbackPath={fallbackPath} />;
  }

  // Si tiene permiso, renderizar el contenido
  return children;
};

/**
 * Página de Acceso Denegado
 */
const AccessDeniedPage = ({ fallbackPath = '/' }) => {
  return (
    <div className="flex items-center justify-center h-full min-h-[500px] p-8">
      <Card className="max-w-md w-full p-8 text-center bg-gradient-to-br from-red-50 to-white border-red-200">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldAlert size={32} className="text-red-600" />
        </div>
        
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Acceso Denegado
        </h1>
        
        <p className="text-slate-600 mb-6">
          No tienes permisos para acceder a esta sección. 
          Contacta al administrador si crees que deberías tener acceso.
        </p>
        
        <div className="flex flex-col gap-3">
          <Button
            onClick={() => window.history.back()}
            variant="outline"
            className="w-full"
          >
            <ArrowLeft size={16} className="mr-2" />
            Volver atrás
          </Button>
          
          <Button
            onClick={() => window.location.href = fallbackPath}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            <Home size={16} className="mr-2" />
            Ir al inicio
          </Button>
        </div>
        
        <p className="text-xs text-slate-400 mt-6">
          Si necesitas acceso, solicítalo a tu administrador del sistema.
        </p>
      </Card>
    </div>
  );
};

export { ProtectedRoute, AccessDeniedPage };
export default ProtectedRoute;
